import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { AuthModalProps } from '../types/auth';

const AuthModal = ({ isOpen, onClose, mode = 'signin' }: AuthModalProps) => {
  const navigate = useNavigate();
  const [currentMode, setCurrentMode] = useState<'signin' | 'signup' | 'confirm'>(mode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { signIn, signUp, confirmSignUp, resendConfirmationCode, signInWithGoogle } = useAuth();

  // Save redirect path when modal opens
  const saveRedirectPath = () => {
    sessionStorage.setItem('auth_redirect_path', window.location.pathname);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    switch (name) {
      case 'email':
        setEmail(value);
        break;
      case 'password':
        setPassword(value);
        break;
      case 'name':
        setName(value);
        break;
      case 'confirmationCode':
        setConfirmationCode(value);
        break;
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      const result = await signIn(email, password);
      if (result.success) {
        setMessage('Sign in successful!');
        setTimeout(() => {
          onClose();
          setMessage('');
        }, 1000);
      } else {
        setMessage(result.error || 'Sign in failed');
      }
    } catch (error) {
      setMessage('An error occurred during sign in');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      setMessage('Google sign-in failed: No credential received');
      return;
    }

    saveRedirectPath(); // Save current path before auth
    setIsLoading(true);
    setMessage('');

    try {
      const result = await signInWithGoogle(credentialResponse.credential);
      if (result.success) {
        setMessage('Google sign-in successful!');
        setTimeout(() => {
          onClose();
          setMessage('');
          // Redirect to saved path
          const redirectPath = sessionStorage.getItem('auth_redirect_path') || '/trips';
          sessionStorage.removeItem('auth_redirect_path');
          
          // Only navigate if the path is different (avoids reload/remount)
          if (window.location.pathname !== redirectPath) {
            navigate(redirectPath);
          }
        }, 1000);
      } else {
        setMessage(result.error || 'Google sign-in failed');
      }
    } catch (error) {
      setMessage('An error occurred during Google sign-in');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    setMessage('Google sign-in was cancelled or failed');
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      const result = await signUp(email, password, name);
      if (result.success) {
        setMessage('Sign up successful! Please check your email for confirmation code.');
        setCurrentMode('confirm');
      } else {
        setMessage(result.error || 'Sign up failed');
      }
    } catch (error) {
      setMessage('An error occurred during sign up');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      const result = await confirmSignUp(email, confirmationCode);
      if (result.success) {
        setMessage('Email confirmed! You can now sign in.');
        setCurrentMode('signin');
        setConfirmationCode('');
      } else {
        setMessage(result.error || 'Confirmation failed');
      }
    } catch (error) {
      setMessage('An error occurred during confirmation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setMessage('');

    try {
      const result = await resendConfirmationCode(email);
      if (result.success) {
        setMessage('Confirmation code resent!');
      } else {
        setMessage(result.error || 'Failed to resend code');
      }
    } catch (error) {
      setMessage('An error occurred while resending code');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-airbnb-800">
            {currentMode === 'signin' && 'Sign In'}
            {currentMode === 'signup' && 'Sign Up'}
            {currentMode === 'confirm' && 'Confirm Email'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {message && (
          <div className={`mb-4 p-3 rounded ${
            message.includes('successful') || message.includes('confirmed')
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {message}
          </div>
        )}

        {currentMode === 'signin' && (
          <>
            {/* Google Sign In Button */}
            <div className="mb-4 space-y-2">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap={false}
                theme="outline"
                size="large"
                text="signin_with"
                shape="rectangular"
                width="100%"
              />
            </div>
            
            {/* Divider */}
            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with email</span>
              </div>
            </div>

            <form onSubmit={handleSignIn} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={email}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={isLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                name="password"
                value={password}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={isLoading}
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </button>
            {/* <p className="text-center text-sm text-gray-600">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => setCurrentMode('signup')}
                className="text-primary-600 hover:text-primary-700"
              >
                Sign Up
              </button>
            </p> */}
          </form>
          </>
        )}

        {currentMode === 'signup' && (
          <form onSubmit={handleSignUp} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                name="name"
                value={name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={isLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={email}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={isLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                name="password"
                value={password}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={isLoading}
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {isLoading ? 'Signing Up...' : 'Sign Up'}
            </button>
            <p className="text-center text-sm text-gray-600">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => setCurrentMode('signin')}
                className="text-primary-600 hover:text-primary-700"
              >
                Sign In
              </button>
            </p>
          </form>
        )}

        {currentMode === 'confirm' && (
          <form onSubmit={handleConfirmSignUp} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirmation Code
              </label>
              <input
                type="text"
                name="confirmationCode"
                value={confirmationCode}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={isLoading}
                placeholder="Enter the code from your email"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {isLoading ? 'Confirming...' : 'Confirm Email'}
            </button>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={isLoading}
              className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 disabled:opacity-50"
            >
              {isLoading ? 'Resending...' : 'Resend Code'}
            </button>
            <p className="text-center text-sm text-gray-600">
              <button
                type="button"
                onClick={() => setCurrentMode('signin')}
                className="text-primary-600 hover:text-primary-700"
              >
                Back to Sign In
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default AuthModal;